/**
 * Generic ArcGIS REST feature fetcher for regional map layers.
 * Routes through `/api/proxy` via `queryArcgisRest` (retries + cache).
 */
import type { EsriFeatureResponse } from './esriJsonToSiteLayers';
import { queryArcgisRest } from './queryArcgisRest';

const DEFAULT_MAX_FEATURES = 300;

export interface FetchArcgisFeaturesArgs {
  lat: number;
  lng: number;
  radiusMeters: number;
  /** Base query URL without search params. */
  queryUrl: string;
  outFields?: string;
  maxFeatures?: number;
  /** Optional boundary envelope instead of point+distance. */
  envelope?: { xmin: number; ymin: number; xmax: number; ymax: number };
  /** Human label for retry / error messages. */
  label?: string;
}

export interface FetchArcgisFeaturesResult {
  features: NonNullable<EsriFeatureResponse['features']>;
  truncated: boolean;
  warning?: string;
}

function buildQueryUrl(args: FetchArcgisFeaturesArgs): string {
  const max = args.maxFeatures ?? DEFAULT_MAX_FEATURES;
  const params = new URLSearchParams({
    where: '1=1',
    outFields: args.outFields ?? '*',
    spatialRel: 'esriSpatialRelIntersects',
    returnGeometry: 'true',
    resultRecordCount: String(max),
    f: 'json',
  });

  if (args.envelope) {
    const { xmin, ymin, xmax, ymax } = args.envelope;
    params.set('geometry', `${xmin},${ymin},${xmax},${ymax}`);
    params.set('geometryType', 'esriGeometryEnvelope');
    params.set('inSR', '4326');
  } else {
    params.set('geometry', `${args.lng},${args.lat}`);
    params.set('geometryType', 'esriGeometryPoint');
    params.set('inSR', '4326');
    params.set('distance', String(Math.round(args.radiusMeters)));
    params.set('units', 'esriSRUnit_Meter');
  }

  const base = args.queryUrl.replace(/\?.*$/, '');
  return `${base}?${params.toString()}`;
}

export async function fetchArcgisFeatures(
  args: FetchArcgisFeaturesArgs,
): Promise<FetchArcgisFeaturesResult> {
  const url = buildQueryUrl(args);
  const json = await queryArcgisRest(url, { label: args.label ?? 'ArcGIS layer' });

  const features = json.features ?? [];
  const truncated = json.exceededTransferLimit === true;
  let warning: string | undefined;
  if (truncated) {
    warning = `Results truncated at ${args.maxFeatures ?? DEFAULT_MAX_FEATURES} features`;
  }

  return { features, truncated, warning };
}

/** Miles → metres for query distance. */
export function milesToMeters(miles: number): number {
  return miles * 1609.344;
}

/** Build an envelope from a boundary polygon expanded by radius metres. */
export function envelopeFromBoundary(
  boundary: { lat: number; lng: number }[],
  bufferMeters: number,
): { xmin: number; ymin: number; xmax: number; ymax: number } {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const p of boundary) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  const midLat = (minLat + maxLat) / 2;
  const dLat = bufferMeters / 111_320;
  const dLng = bufferMeters / (111_320 * Math.max(0.01, Math.cos((midLat * Math.PI) / 180)));
  return {
    xmin: minLng - dLng,
    ymin: minLat - dLat,
    xmax: maxLng + dLng,
    ymax: maxLat + dLat,
  };
}
